import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Grid, Button, Select, MenuItem, InputLabel, FormControl, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';

import { useSelectFilterStyles } from 'shared/styles/selectFilterStyles/selectFilterStyles';
import { useSmallSelectStyles } from 'shared/styles/smallSelectStyles/smallSelectStyles';

import { shopProductsFilters } from '../../shared/constants/shopProductFilters';
import { shopProductsCategories } from '../../shared/constants/shopProductsCategories';
import { setShopFiltersAction } from '../../store/shopActions';
import { getProductsProperties } from '../../selectors/shopSelectors';

const useStyle = makeStyles((theme) => ({
  button: {
    height: 45,
    padding: '0 15px',
    marginRight: 10,
    fontSize: '0.875rem',
  },
  formControl: {
    marginTop: 0,
    marginRight: theme.spacing(1),
    minWidth: 120,

    '& fieldset': {
      top: 0,
    },

    '& legend': {
      display: 'none',
    },
  },
  label: {
    top: -16,
    marginLeft: 15,
    textTransform: 'uppercase',
    fontWeight: 500,
    fontSize: '0.8125rem',
  },
  container: {
    position: 'relative',
    marginBottom: theme.spacing(2),
  },
}));

const initialFilters = {
  regions: '',
  segments: '',
  categories: '',
};

const ShopFilters = ({ children, showSelectFilters, onFilterChange }) => {
  const { t } = useTranslation();
  const classes = useStyle();
  const selectFilterClasses = useSelectFilterStyles();
  const smallSelectClasses = useSmallSelectStyles();

  const dispatch = useDispatch();

  const { filters, activeView } = useSelector(({ shop }) => shop);
  const productProperties = useSelector(getProductsProperties);

  const handleCategoryFilterChange = (value) => {
    const { categories } = filters;
    const updatedFilters = {
      filters: {
        ...initialFilters,
        categories: categories === value ? '' : value,
      },
    };

    dispatch(setShopFiltersAction(updatedFilters));
    onFilterChange(updatedFilters);
  };

  const handleSelectFilterChange = (filter, value) => {
    const updatedFilters = {
      filters: {
        ...filters,
        [filter]: value,
      },
    };

    dispatch(setShopFiltersAction(updatedFilters));
    onFilterChange(updatedFilters);
  };

  return (
    <>
      <Box className={classes.container}>
        <Grid container justifyContent={activeView ? 'flex-end' : 'space-between'}>
          {!activeView && [
            <Grid item key="categories">
              <Button
                key="all"
                variant={filters.categories ? 'outlined' : 'contained'}
                color="primary"
                className={classes.button}
                size="small"
                onClick={() => handleCategoryFilterChange(filters.categories)}
              >
                {t('All')}
              </Button>
              {productProperties.data.categories.map(({ name }) => (
                <Button
                  key={name}
                  variant={filters.categories === name ? 'contained' : 'outlined'}
                  color="primary"
                  className={classes.button}
                  size="small"
                  onClick={() => handleCategoryFilterChange(name)}
                >
                  {name}
                </Button>
              ))}
            </Grid>,
            <Grid item key="free">
              <Grid container justifyContent="center" alignContent="center">
                <Grid item>
                  <Button
                    key="free-trial"
                    variant={
                      filters.categories === shopProductsCategories.trial ? 'contained' : 'outlined'
                    }
                    color="primary"
                    className={classes.button}
                    size="small"
                    onClick={() => handleCategoryFilterChange(shopProductsCategories.trial)}
                  >
                    {t('Free trial')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    key="free-products"
                    variant={
                      filters.categories === shopProductsCategories.free ? 'contained' : 'outlined'
                    }
                    color="primary"
                    className={classes.button}
                    size="small"
                    onClick={() => handleCategoryFilterChange(shopProductsCategories.free)}
                  >
                    {t('Free of charge')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>,
          ]}

          <Grid item>{children}</Grid>
        </Grid>
      </Box>
      {showSelectFilters && (
        <Grid container>
          <Grid item>
            <FormControl className={classes.formControl}>
              <InputLabel className={classes.label} shrink={false}>
                {t('Region')}
              </InputLabel>
              <Select
                value={filters.regions}
                onChange={(event) =>
                  handleSelectFilterChange(shopProductsFilters.regions, event.target.value)
                }
                label={t('Region')}
                classes={filters.regions ? selectFilterClasses : smallSelectClasses}
              >
                <MenuItem key="empty" value="">
                  {t('None')}
                </MenuItem>
                {productProperties.data.regions.map(({ name }) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel className={classes.label} shrink={false}>
                {t('Segment')}
              </InputLabel>
              <Select
                value={filters.segments}
                onChange={(event) =>
                  handleSelectFilterChange(shopProductsFilters.segments, event.target.value)
                }
                label={t('Segment')}
                classes={filters.segments ? selectFilterClasses : smallSelectClasses}
              >
                <MenuItem key="empty" value="">
                  {t('None')}
                </MenuItem>
                {productProperties.data.segments.map(({ name }) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel className={classes.label} shrink={false}>
                {t('Category')}
              </InputLabel>
              <Select
                value={filters.categories}
                onChange={(event) =>
                  handleSelectFilterChange(shopProductsFilters.categories, event.target.value)
                }
                label={t('Category')}
                classes={filters.categories ? selectFilterClasses : smallSelectClasses}
              >
                <MenuItem key="empty" value="">
                  {t('None')}
                </MenuItem>
                {productProperties.data.categories.map(({ name }) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
    </>
  );
};

ShopFilters.defaultProps = {
  children: null,
  showSelectFilters: true,
  onFilterChange: () => {},
};

ShopFilters.propTypes = {
  children: PropTypes.node,
  showSelectFilters: PropTypes.bool,
  onFilterChange: PropTypes.func,
};

export { ShopFilters };
